import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCohortEnrollment } from './useParticipantData';
import { logAuditAction } from '@/hooks/useAuditLog';

// -------- Types --------

export interface ContentItem {
  id: string;
  name: string;
  content_type: string | null;
  duration: string | null;
  description: string | null;
  url: string | null;
  tags: string[] | null;
  thumbnail_url: string | null;
  author: string | null;
  access_level: string;
  vimeo_id: string | null;
  vimeo_uri: string | null;
  vimeo_player_url: string | null;
  html_content: string | null;
  target_levels: number[] | null;
  sort_order: number | null;
}

export interface ChapterWithContent {
  id: string;
  name: string;
  week_number: number | null;
  sort_order: number | null;
  contents: {
    content: ContentItem;
    is_mandatory: boolean;
    sort_order: number | null;
  }[];
}

export interface ContentProgressRecord {
  content_id: string;
  completed: boolean | null;
  progress_percent: number | null;
  completed_at: string | null;
}

// -------- Hooks --------

/** Fetch all chapters with their content items for a given program (or auto-detect from enrollment) */
export function useChaptersWithContent(explicitProgramId?: string | null) {
  const enrollment = useCohortEnrollment();
  const enrolledProgramId = (enrollment.data?.cohorts as any)?.programs?.id as string | undefined;
  const programId = explicitProgramId ?? enrolledProgramId;

  return useQuery({
    queryKey: ['chapters-with-content', programId],
    queryFn: async (): Promise<ChapterWithContent[]> => {
      // 1. Fetch chapters for the program
      const { data: chapters, error: chErr } = await supabase
        .from('program_chapters')
        .select('id, name, week_number, sort_order')
        .eq('program_id', programId!)
        .order('sort_order', { ascending: true });

      if (chErr) throw chErr;
      if (!chapters || chapters.length === 0) return [];

      // 2. Fetch chapter_contents with joined content_bank
      const chapterIds = chapters.map((c) => c.id);
      const { data: ccRows, error: ccErr } = await supabase
        .from('chapter_contents')
        .select('chapter_id, content_id, is_mandatory, sort_order, content_bank(*)')
        .in('chapter_id', chapterIds)
        .order('sort_order', { ascending: true });

      if (ccErr) throw ccErr;

      // 3. Build the structure
      return chapters.map((ch) => ({
        id: ch.id,
        name: ch.name,
        week_number: ch.week_number,
        sort_order: ch.sort_order,
        contents: (ccRows ?? [])
          .filter((cc) => cc.chapter_id === ch.id && cc.content_bank != null && (cc.content_bank as any).planning_status === 'published')
          .map((cc) => ({
            content: cc.content_bank as unknown as ContentItem,
            is_mandatory: cc.is_mandatory ?? false,
            sort_order: cc.sort_order,
          })),
      }));
    },
    enabled: !!programId,
  });
}

/** Fetch all content progress for the current user */
export function useAllContentProgress() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['all-content-progress', user?.id],
    queryFn: async (): Promise<ContentProgressRecord[]> => {
      const { data, error } = await supabase
        .from('content_progress')
        .select('content_id, completed, progress_percent, completed_at')
        .eq('user_id', user!.id);

      if (error) throw error;
      return (data ?? []) as ContentProgressRecord[];
    },
    enabled: !!user?.id,
  });
}

/** Upsert content progress (mark complete, update percent) */
export function useUpdateContentProgress() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      content_id: string;
      completed?: boolean;
      progress_percent?: number;
    }) => {
      const { error } = await supabase
        .from('content_progress')
        .upsert(
          {
            user_id: user!.id,
            content_id: input.content_id,
            completed: input.completed ?? false,
            progress_percent: input.progress_percent ?? 0,
            completed_at: input.completed ? new Date().toISOString() : null,
          },
          { onConflict: 'user_id,content_id' }
        );

      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['all-content-progress', user?.id] });
      qc.invalidateQueries({ queryKey: ['content-progress', user?.id] });
      // Progreso semanal / widget del inicio.
      qc.invalidateQueries({ queryKey: ['weekly-progress'] });
      if (user && variables.completed) {
        logAuditAction(user.id, 'content_view', 'Contenido completado', { content_id: variables.content_id } as any);
      }
    },
  });
}

/** Fetch all content_bank items (for admin or browsing) */
export function useContentBank() {
  return useQuery({
    queryKey: ['content-bank'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_bank')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Create a content_bank item */
export function useCreateContent() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      content_type?: string | null;
      duration?: string | null;
      description?: string | null;
      url?: string | null;
      tags?: string[] | null;
      access_level?: string;
      thumbnail_url?: string | null;
      author?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('content_bank')
        // Items created from the classic Bank form are uploaded ready-to-go,
        // so default them to 'published' (planning items use useContentPlanning hook).
        .insert({ planning_status: 'published', ...input } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['content-bank'] });
      if (user) logAuditAction(user.id, 'content_create', `Contenido creado: ${data.name}`, { content_id: data.id } as any);
    },
  });
}

/** Update a content_bank item */
export function useUpdateContent() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; [key: string]: any }) => {
      const { data, error } = await supabase
        .from('content_bank')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['content-bank'] });
      if (user) logAuditAction(user.id, 'content_update', `Contenido actualizado: ${data.name}`, { content_id: data.id } as any);
    },
  });
}

/** Delete a content_bank item */
export function useDeleteContent() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('content_bank')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['content-bank'] });
      if (user) logAuditAction(user.id, 'content_delete', 'Contenido eliminado', { content_id: id } as any);
    },
  });
}

/** Fetch all content grouped by category, filtered by access level */
export function useAllContentByCategory(_hasPaidAccess?: boolean) {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  return useQuery({
    queryKey: ['all-content-by-category', isAuthenticated ? 'auth' : 'anon'],
    queryFn: async () => {
      // Authenticated users get full content_bank rows (incl. url, html_content,
      // vimeo_player_url, description, …) so the player modals can render real
      // content. Visitors keep the restricted public_content_catalog view.
      const itemsPromise = isAuthenticated
        ? supabase
            .from('content_bank')
            .select('*')
            .eq('planning_status', 'published')
            // "Solo ruta" (program_only) content is exclusive to its assigned
            // route/cohort, so it must not appear in the platform-wide catalog.
            .neq('access_level', 'program_only')
            .order('sort_order', { ascending: true })
            .order('name')
        : (supabase as any)
            .from('public_content_catalog')
            .select('*')
            .order('sort_order', { ascending: true })
            .order('name');

      const [catRes, contentRes] = await Promise.all([
        supabase.from('content_categories').select('name').order('sort_order'),
        itemsPromise,
      ]);

      if (catRes.error) throw catRes.error;
      if (contentRes.error) throw contentRes.error;

      // Group items by category. "Solo ruta" (program_only) items are excluded
      // from the platform-wide catalog (they live inside their assigned route);
      // the authenticated query already filters them, the public view does not.
      const byCategory: Record<string, ContentItem[]> = {};
      (contentRes.data ?? [])
        .filter((item: any) => item.access_level !== 'program_only')
        .forEach((item: any) => {
        const cat = item.category || 'Sin categoría';
        if (!byCategory[cat]) byCategory[cat] = [];
        byCategory[cat].push(item as ContentItem);
      });

      // Build ordered result following content_categories sort_order
      const ordered: Record<string, ContentItem[]> = {};
      (catRes.data ?? []).forEach((c) => {
        if (byCategory[c.name]) {
          ordered[c.name] = byCategory[c.name];
          delete byCategory[c.name];
        }
      });
      // Append remaining categories not in the table
      Object.entries(byCategory).forEach(([cat, items]) => {
        if (cat !== 'Sin categoría') ordered[cat] = items;
      });
      // "Sin categoría" always last
      if (byCategory['Sin categoría']) {
        ordered['Sin categoría'] = byCategory['Sin categoría'];
      }

      return ordered;
    },
  });
}

